import {
  generateProfileTokenApiV1UnifiedGenerateProfileTokenPost,
  getDeeplinkOnboardingLanding,
  getInitialOnboardingView,
  getOnboardingEligibility,
  magicMockApiV1UnifiedProfilesPost,
  mapEligibilityToStepCompletion,
  studentsControllerGetEnrolledPrograms,
  type OnboardingEligibility,
} from "@oneapp/data";
import {
  getVerticalIdFromEnrolledProgram,
  isNonVerticalOne,
  ROUTESWEB,
} from "@repo/utils";

import {
  useAuthStore,
  type EnrolledProgram,
  type ProfilesData,
} from "../../../store/useAuthStore";
import type { DeeplinkRoute, ModuleHandler, ParsedLink } from "../types";

type ProfilesApiResponse = {
  access_token?: string;
  refresh_token?: string;
  unified_account_details?: ProfilesData["unified_account_details"];
};

function getGatewayHeaders(authorization: string) {
  const token = authorization.replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: authorization,
    accesstoken: token,
    platform: "web",
    "product-id": import.meta.env.VITE_PRODUCT_ID ?? "300",
    "tenant-id": import.meta.env.VITE_TENANT_ID ?? "1",
    "ot-tenant": "infinitylearn",
    "x-tenant": "infinitylearn",
    "x-platform": "web",
    "x-product": "1",
  };
}

function resolveVerticalId(): number | null {
  const programs = useAuthStore.getState().enrolledPrograms ?? [];
  const current =
    programs.find((p) => p.isDefault === true) ?? programs[0] ?? null;
  const fromProgram = getVerticalIdFromEnrolledProgram(current);
  if (fromProgram != null) return fromProgram;
  const profiles =
    useAuthStore.getState().profilesData?.unified_account_details?.profiles ??
    [];
  const profile =
    profiles.find((p) => p.is_default_profile === true) ?? profiles[0];
  const v = profile?.vertical_id;
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

async function switchProfileByUamIdIfNeeded(
  targetUamIdRaw: string,
): Promise<boolean> {
  const targetUamId = Number(targetUamIdRaw.trim());
  if (!Number.isFinite(targetUamId) || targetUamId <= 0) {
    console.warn("[profile-update deeplink] invalid uamId");
    return false;
  }

  const state = useAuthStore.getState();
  const unifiedAccountId =
    state.profilesData?.unified_account_details?.unified_account_id;
  if (!unifiedAccountId) return false;

  const profiles = state.profilesData?.unified_account_details?.profiles ?? [];
  const targetProfile = profiles.find((p) => p.uam_id === targetUamId);
  if (!targetProfile) {
    console.warn(
      "[profile-update deeplink] target uam_id not in profiles list",
    );
    return false;
  }

  const activeUamId =
    profiles.find((p) => p.is_default_profile)?.uam_id ?? profiles[0]?.uam_id;
  if (activeUamId === targetUamId) return true;

  const token = state.token ?? state.transientAccessToken;
  if (!token) return false;

  const response = (await magicMockApiV1UnifiedProfilesPost(
    { unified_account_id: unifiedAccountId } as never,
    {
      clientName: "gateway",
      headers: getGatewayHeaders(`Bearer ${token}`),
    },
  )) as ProfilesApiResponse;

  const refreshedProfiles = response?.unified_account_details?.profiles ?? [];
  const selectedProfile =
    refreshedProfiles.find((p) => p.uam_id === targetUamId) ?? targetProfile;
  const ac2token = response?.access_token;
  if (!ac2token) return false;

  const profilesPayload = response as ProfilesData;
  state.setProfilesData({
    ...profilesPayload,
    unified_account_details: {
      ...profilesPayload?.unified_account_details,
      profiles:
        selectedProfile && refreshedProfiles.length > 0
          ? [
              selectedProfile,
              ...refreshedProfiles.filter((p) => p !== selectedProfile),
            ]
          : refreshedProfiles,
    },
  });

  const genData =
    (await generateProfileTokenApiV1UnifiedGenerateProfileTokenPost(
      { unified_account_id: unifiedAccountId, uam_id: targetUamId },
      undefined,
      {
        clientName: "learnGateway",
        headers: getGatewayHeaders(`Bearer ${ac2token}`),
      },
    )) as { access_token?: string; refresh_token?: string };

  if (!genData.access_token) return false;

  const details = response?.unified_account_details;
  if (details) state.setUnifiedAccountDetails(details);

  const gradeId =
    (
      details?.user_account_details as
        | { grade?: { gradeId?: number } }
        | undefined
    )?.grade?.gradeId ?? 0;
  const examsParam = selectedProfile?.exams
    ?.map((e) => e.examId)
    .filter((id): id is number => id != null)
    .join(",");

  try {
    const enrolledResponse = (await studentsControllerGetEnrolledPrograms(
      {
        grade: gradeId,
        processEnrollment: true,
        ...(examsParam ? { exams: examsParam } : {}),
      },
      {
        clientName: "oneApp",
        headers: {
          Authorization: `Bearer ${genData.access_token}`,
          accesstoken: genData.access_token,
          "x-platform": "web",
          "x-tenant": "infinitylearn",
        },
      },
    )) as { data?: { programs?: EnrolledProgram[] } } | undefined;
    const programs = enrolledResponse?.data?.programs;
    if (programs && Array.isArray(programs)) {
      state.setEnrolledPrograms(programs);
    }
  } catch (e) {
    console.warn("[profile-update deeplink] enrolled programs failed", e);
  }

  const uad = details?.user_account_details as
    | {
        first_name?: string;
        last_name?: string;
        user_type?: { name?: string };
        role?: { role_name?: string };
      }
    | undefined;
  const name =
    `${uad?.first_name ?? ""} ${uad?.last_name ?? ""}`.trim() || "User";
  const roleName = uad?.user_type?.name || uad?.role?.role_name;

  state.login(
    genData.access_token,
    {
      id: details?.unified_account_id ?? unifiedAccountId,
      name,
      role: roleName?.toLowerCase() === "guest" ? "guest" : "student",
    },
    genData.refresh_token,
    true,
    { skipAinaIntroPrime: true },
  );

  return true;
}

function buildProfileSetupState(eligibility: OnboardingEligibility) {
  const completion = mapEligibilityToStepCompletion(eligibility);
  const initialView = getInitialOnboardingView(completion);
  return {
    fromDeeplink: true as const,
    initialView,
    completion,
    eligibility,
  };
}

export const profileUpdateHandler: ModuleHandler = async (
  parsed: ParsedLink,
) => {
  const dashboard: DeeplinkRoute = { path: `/${ROUTESWEB.APP.DASHBOARD}` };
  const targetUamId = parsed.userId?.trim();
  if (!targetUamId) {
    console.warn("[profile-update deeplink] missing userId");
    return dashboard;
  }

  const switched = await switchProfileByUamIdIfNeeded(targetUamId);
  if (!switched) return dashboard;

  const verticalId = resolveVerticalId();
  if (verticalId == null || isNonVerticalOne(verticalId)) {
    return dashboard;
  }

  const studentGatewayId = targetUamId;

  try {
    const eligibilityRes = await getOnboardingEligibility({
      userId: String(studentGatewayId),
      platform: "web",
    });
    const data = eligibilityRes?.data;
    if (!data) return dashboard;

    const landing = getDeeplinkOnboardingLanding(data);
    if (landing === "dashboard") return dashboard;

    if (landing === "intro") {
      return {
        path: `/${ROUTESWEB.APP.PAID_USER_ONBOARDING}`,
        state: {
          fromDeeplink: true,
          eligibility: data,
          completion: mapEligibilityToStepCompletion(data),
        },
      };
    }

    const state = buildProfileSetupState(data);
    return {
      path: `/${ROUTESWEB.APP.PROFILE_SETUP}`,
      state: {
        ...state,
        initialView: landing,
      },
    };
  } catch (e) {
    console.warn("[profile-update deeplink] eligibility fetch failed", e);
    return dashboard;
  }
};
